import React from 'react';

import type { AssistantMessage as AssistantMessageModel } from '../types';
import MarkdownBody from '../MarkdownBody';
import ToolChip from './ToolChip';
import QuestionBlock from './QuestionBlock';
import ActionCard from './ActionCard';
import ViewCommandChip from './ViewCommandChip';
import FailCard from './FailCard';

/*
 * Assistant turn: tool-call chips stacked above the body, then the markdown
 * body (with a blinking caret while streaming, or typing dots before the first
 * delta), then any structured extras (question / action card / view-command).
 * A FAILED turn renders the fail+retry card instead of a body.
 */
// Internal tool calls that drive UX (questions/actions) rather than real tool
// work — hidden from the chip list. Their result summaries use `__SENTINEL__`.
const isInternalTool = (name?: string, summary?: string) =>
  name === 'ask_user_question' ||
  name === 'request_action_confirmation' ||
  /^__.*__$/.test(summary || '');

const AssistantMessage = ({ message }: { message: AssistantMessageModel }) => {
  const visibleTools = message.tool_calls.filter(
    (tc) => !isInternalTool(tc.name, tc.result_summary)
  );
  const hasContent = Boolean(message.content);
  const actions = message.actions ?? [];
  const hasExtras = Boolean(
    message.question || actions.length || message.view_command
  );
  // Typing dots only while genuinely waiting — not once the turn produced a
  // question/action/view-command or finished.
  const showTyping =
    !hasContent &&
    !hasExtras &&
    !message.error_detail &&
    message.status !== 'FAILED' &&
    message.status !== 'COMPLETED' &&
    message.status !== 'CANCELLED';

  // Nothing to show yet (e.g. a bare stub) and not actively typing → render nothing.
  if (
    !hasContent &&
    !hasExtras &&
    visibleTools.length === 0 &&
    !showTyping &&
    message.status !== 'FAILED'
  ) {
    return null;
  }

  // When an action card is present, split the body at the point the first card
  // arrived so text streamed after it renders below the card, not above.
  const splitAt =
    actions.length && message.actionContentLength != null
      ? message.actionContentLength
      : null;
  const before =
    splitAt != null ? message.content.slice(0, splitAt) : message.content;
  const after = splitAt != null ? message.content.slice(splitAt) : '';
  const caret = message.streaming ? <span className="bra-caret" /> : null;

  return (
    <div className="bra-msg bra-a">
      {visibleTools.length > 0 ? (
        <div className="bra-chips">
          {visibleTools.map((tc) => (
            <ToolChip key={tc.id} tool={tc} />
          ))}
        </div>
      ) : null}

      {message.status === 'FAILED' ? (
        <FailCard message={message} />
      ) : (
        <>
          <div className="bra-body">
            {showTyping ? (
              <span className="bra-typing">
                <i />
                <i />
                <i />
              </span>
            ) : (
              <>
                <MarkdownBody content={before} />
                {/* caret stays on the before-body only when nothing follows it */}
                {splitAt == null && !message.question ? caret : null}
              </>
            )}
          </div>

          {message.question ? (
            <QuestionBlock question={message.question} />
          ) : null}
          {actions.map((a) => (
            <ActionCard key={a.external_id} action={a} />
          ))}

          {splitAt != null && (after || message.streaming) ? (
            <div className="bra-body">
              <MarkdownBody content={after} />
              {caret}
            </div>
          ) : null}

          {message.view_command ? (
            <ViewCommandChip command={message.view_command} />
          ) : null}
        </>
      )}
    </div>
  );
};

export default AssistantMessage;
